import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { BlogCoversButton } from '../../components/admin/BlogCoversButton';
import { SocialHistory } from '../../components/admin/SocialHistory';
import { PublishNowButtons } from '../../components/admin/PublishNowButtons';
import { Copy, RefreshCw, Check, Trash2, Download, Facebook, Sparkles, Clock, Tag, ChevronDown, ChevronUp, Image as ImageIcon, Lightbulb, Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFBPosts = () => {
  const { getAuthHeader } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingCustom, setGeneratingCustom] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchPosts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/fb-posts${qs}`, { headers: getAuthHeader() });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filter, categoryFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/fb-posts/generate`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success(res.data.message || `${res.data.count} publication(s) en cours de création...`);
        fetchPosts();
        // Auto-refresh while generating
        const interval = setInterval(() => fetchPosts(), 5000);
        setTimeout(() => clearInterval(interval), 120000);
      } else {
        toast.error(res.data.message || 'Aucun véhicule éligible');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCustom = async () => {
    setGeneratingCustom(true);
    try {
      const res = await axios.post(`${API}/fb-posts/generate-custom`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success(`Contenu "${res.data.post?.category === 'tip' ? 'Conseil auto' : 'Actualité auto'}" en cours de création...`);
        fetchPosts();
        // Auto-refresh while generating
        const interval = setInterval(() => fetchPosts(), 5000);
        setTimeout(() => clearInterval(interval), 120000);
      } else {
        toast.error(res.data.message || 'Erreur de génération');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération du contenu');
    } finally {
      setGeneratingCustom(false);
    }
  };

  const handleCopyText = async (post) => {
    try {
      await navigator.clipboard.writeText(post.text);
      setCopiedId(post.id);
      toast.success('Texte copié!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = post.text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(post.id);
      toast.success('Texte copié!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleMarkPosted = async (postId) => {
    try {
      await axios.put(`${API}/fb-posts/${postId}/mark-posted`, {}, { headers: getAuthHeader() });
      toast.success('Marqué comme publié!');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleRegenerate = async (postId) => {
    setRegeneratingId(postId);
    try {
      const res = await axios.post(`${API}/fb-posts/${postId}/regenerate`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success('Texte régénéré!');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Erreur lors de la régénération');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cette publication?')) return;
    try {
      await axios.delete(`${API}/fb-posts/${postId}`, { headers: getAuthHeader() });
      toast.success('Publication supprimée');
      fetchPosts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadImages = async (post) => {
    const allImages = [];
    (post.montage_urls || []).forEach((url, i) => {
      allImages.push({ url, name: `${post.stock_number}_montage_${i + 1}.jpg` });
    });
    (post.images || []).forEach((img, i) => {
      allImages.push({ url: img, name: `${post.stock_number}_photo_${i + 1}.jpg` });
    });
    
    if (!allImages.length) {
      toast.error('Aucune image disponible');
      return;
    }
    for (const img of allImages) {
      const link = document.createElement('a');
      link.href = img.url;
      link.target = '_blank';
      link.download = img.name;
      link.click();
      await new Promise(r => setTimeout(r, 300));
    }
    toast.success(`${allImages.length} image(s) téléchargée(s)`);
  };

  const getPriorityBadge = (reason, postCategory) => {
    if (postCategory === 'tip') return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 flex items-center gap-1"><Lightbulb className="w-3 h-3" />Conseil auto</span>;
    if (postCategory === 'news') return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700 flex items-center gap-1"><Newspaper className="w-3 h-3" />Actualité auto</span>;
    const badges = {
      new_arrival: { label: 'Nouvel arrivage', className: 'bg-green-100 text-green-700' },
      price_drop: { label: 'Baisse de prix', className: 'bg-orange-100 text-orange-700' },
      featured: { label: 'En vedette', className: 'bg-blue-100 text-blue-700' },
      rotation: { label: 'Rotation', className: 'bg-slate-100 text-slate-600' },
    };
    const badge = badges[reason] || badges.rotation;
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>{badge.label}</span>;
  };

  const readyCount = posts.filter(p => !p.posted).length;
  const postedCount = posts.filter(p => p.posted).length;
  const customCount = posts.filter(p => p.post_category === 'tip' || p.post_category === 'news').length;
  const inventoryCount = posts.filter(p => !p.post_category).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-fb-posts">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Facebook className="w-6 h-6 text-blue-600" />
              Publications Facebook
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Inventaire + contenu auto (conseils & actualités) · Copiez et publiez sur votre page FB
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <BlogCoversButton />
            <button
              onClick={handleGenerateCustom}
              disabled={generatingCustom}
              data-testid="generate-custom-fb-btn"
              className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors font-medium"
            >
              {generatingCustom ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
              {generatingCustom ? 'Création...' : 'Contenu auto'}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-fb-posts-btn"
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
            >
              {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? 'Génération...' : 'Inventaire'}
            </button>
          </div>
        </div>

        <PublishNowButtons />

        <SocialHistory />

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{posts.length}</div>
            <div className="text-sm text-slate-500">Total</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-amber-600">{readyCount}</div>
            <div className="text-sm text-slate-500">À publier</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-green-600">{postedCount}</div>
            <div className="text-sm text-slate-500">Publiées</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-purple-600">{customCount}</div>
            <div className="text-sm text-slate-500">Contenu auto</div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { value: 'all', label: 'Tout' },
              { value: 'inventory', label: 'Inventaire' },
              { value: 'custom', label: 'Contenu auto' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setCategoryFilter(f.value)}
                data-testid={`category-filter-${f.value}`}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${categoryFilter === f.value ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { value: 'all', label: 'Toutes' },
              { value: 'ready', label: 'À publier' },
              { value: 'posted', label: 'Publiées' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filter === f.value ? 'bg-white text-slate-900 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Posts list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Facebook className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-lg">Aucune publication générée</p>
            <p className="text-slate-400 text-sm mt-1">Cliquez sur "Générer maintenant" pour créer vos premières publications</p>
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => (
              <div key={post.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${post.status === 'generating' ? 'border-blue-300 animate-pulse' : post.posted ? 'border-green-200 opacity-75' : 'border-slate-200'}`} data-testid={`fb-post-${post.id}`}>
                {/* Generating indicator */}
                {post.status === 'generating' && (
                  <div className="bg-blue-50 px-4 py-2 flex items-center gap-2 text-blue-600 text-sm font-medium border-b border-blue-100">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Génération en cours... le texte et l'image seront prêts dans quelques secondes
                  </div>
                )}
                {/* Post header */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Thumbnail */}
                    {(post.montage_urls?.[0] || post.images?.[0]) ? (
                      <img src={post.montage_urls?.[0] || post.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-6 h-6 text-slate-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{post.vehicle_title}</h3>
                        {post.stock_number && <span className="text-xs text-slate-400">#{post.stock_number}</span>}
                        {getPriorityBadge(post.priority_reason, post.post_category)}
                        {post.posted && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Publié</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        {post.price > 0 && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{post.price.toLocaleString('fr-CA')}$</span>}
                        {(post.images?.length > 0 || post.montage_urls?.length > 0) && (
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            {post.images?.length || 0} photos{post.montage_urls?.length ? ` + ${post.montage_urls.length} ${post.post_category ? 'image' : 'montage'}${post.montage_urls.length > 1 ? 's' : ''}` : ''}
                          </span>
                        )}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>
                  
                  <button onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)} className="text-slate-400 hover:text-slate-600 p-1">
                    {expandedPost === post.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>

                {/* Expanded content */}
                {expandedPost === post.id && (
                  <div className="border-t border-slate-100">
                    {/* Montage Images */}
                    {post.montage_urls?.length > 0 && (
                      <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-semibold text-slate-700">
                            {post.post_category ? 'Image AI' : `Images Montage (${post.montage_urls.length})`}
                          </span>
                        </div>
                        <div className={`grid gap-3 ${post.montage_urls.length === 1 ? 'grid-cols-1 max-w-xl' : 'grid-cols-1 md:grid-cols-2'}`}>
                          {post.montage_urls.map((url, idx) => (
                            <div key={idx} className="relative group">
                              <img src={url} alt={`Montage ${idx + 1}`} className="w-full rounded-lg shadow-md border border-slate-200" data-testid={`montage-img-${post.id}-${idx}`} />
                              {!post.post_category && (
                                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                  {idx === 0 ? 'Hero' : idx === 1 ? 'Spotlight' : idx === 2 ? 'Strip' : 'Collage'}
                                </div>
                              )}
                              <a href={url} target="_blank" rel="noreferrer" download className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-700 p-1.5 rounded-lg shadow opacity-0 group-hover:opacity-100 transition-opacity">
                                <Download className="w-4 h-4" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Original vehicle photos */}
                    {post.images?.length > 0 && (
                      <div className="p-4 bg-slate-50 border-b border-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <ImageIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-xs font-medium text-slate-500">Photos originales</span>
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {post.images.map((img, i) => (
                            <img key={i} src={img} alt={`Photo ${i + 1}`} className="h-24 w-auto rounded-lg object-cover flex-shrink-0" />
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Post text */}
                    <div className="p-4">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-96 overflow-y-auto">
                        {post.text}
                      </pre>
                    </div>

                    {/* Actions */}
                    <div className="p-4 pt-0 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleCopyText(post)}
                        data-testid={`copy-text-${post.id}`}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {copiedId === post.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copiedId === post.id ? 'Copié!' : 'Copier le texte'}
                      </button>
                      
                      {post.images?.length > 0 && (
                        <button
                          onClick={() => handleDownloadImages(post)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Photos ({post.images.length})
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleRegenerate(post.id)}
                        disabled={regeneratingId === post.id}
                        className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${regeneratingId === post.id ? 'animate-spin' : ''}`} />
                        Régénérer
                      </button>
                      
                      {!post.posted && (
                        <button
                          onClick={() => handleMarkPosted(post.id)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Marquer publié
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 transition-colors ml-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFBPosts;
